import React, { useEffect, useState } from "react";
import { useDistrict } from "@/context/DistrictContext";
import { fetchActivity } from "@/lib/api";
import { SectionHeader, Reveal, FlatCard } from "@/components/primitives";
import { motion } from "framer-motion";
import {
    AlertTriangle, Activity, Phone, Mail, MapPin, ExternalLink,
    TrendingUp, Building, ScrollText, Users, Sparkles,
} from "lucide-react";

const sevColor = {
    warning: "#E6A123",
    info: "#5C7E9B",
    success: "#5B7E54",
};

const Gauge = ({ percent, label }) => (
    <div className="flex flex-col items-center gap-3" data-testid={`gauge-${label}`}>
        <div
            className="ring-gauge"
            style={{ "--p": percent, "--size": "108px", "--thick": "8px" }}
        >
            <div className="font-display text-2xl ink relative z-10">{percent}</div>
        </div>
        <div className="font-data text-[10px] tracking-[0.18em] uppercase ink-mute text-center max-w-[140px]">{label}</div>
    </div>
);

const SDGRing = ({ goal, name, progress }) => (
    <div className="flex flex-col items-center gap-2" data-testid={`sdg-${goal}`}>
        <div className="ring-gauge" style={{ "--p": progress, "--size": "92px", "--thick": "6px" }}>
            <div className="font-display text-xl">{progress}<span className="text-xs ink-mute">%</span></div>
        </div>
        <div className="font-data text-[10px] tracking-[0.16em] uppercase ink-mute">SDG {goal}</div>
        <div className="font-display text-sm">{name}</div>
    </div>
);

const Dashboard = () => {
    const { district, loading } = useDistrict();
    const [live, setLive] = useState(null);

    useEffect(() => {
        if (!district) return;
        const slug = district.slug;
        const load = () => fetchActivity(slug).then(setLive).catch(() => {});
        load();
        const id = setInterval(load, 12000);
        return () => clearInterval(id);
    }, [district]);

    if (loading || !district) {
        return <div className="min-h-[60vh] grid place-items-center font-display text-2xl italic ink-mute">Loading dashboard…</div>;
    }

    const d = district.dashboard;
    const alerts = live?.alerts || d.alerts;
    const feed = live?.activity_feed || d.activity_feed;
    const accent = district.accent;

    return (
        <div className="bg-ivory">
            {/* Live alerts ribbon */}
            <div className="bg-ink text-ivory overflow-hidden hairline-b" data-testid="live-alerts-ribbon">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-3 flex items-center gap-4">
                    <span className="flex items-center gap-2 shrink-0">
                        <span className="live-dot" />
                        <span className="font-data text-[10px] tracking-[0.24em] uppercase">Live · {district.name}</span>
                    </span>
                    <div className="flex-1 overflow-hidden">
                        <div className="marquee-track gap-12">
                            {[...alerts, ...alerts].map((a, i) => (
                                <span key={i} className="font-body text-sm">
                                    <span className="font-data text-[10px] tracking-[0.18em] uppercase mr-2" style={{ color: sevColor[a.severity] || "#E6A123" }}>{a.severity}</span>
                                    <span className="font-medium">{a.title}.</span>
                                    <span className="ml-2 opacity-80">{a.message}</span>
                                    <span className="mx-6 opacity-30">·</span>
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16">
                <SectionHeader
                    kicker={`${district.name} · Real-Time Governance`}
                    title="Administrative Intelligence"
                    lede="Live indicators, scheme utilisation and conservation status — the district as a living dashboard."
                />

                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-12" data-testid="kpi-grid">
                    {d.kpis.map((k, i) => (
                        <Reveal key={k.label} delay={i}>
                            <FlatCard className="p-5">
                                <div className="font-data text-[10px] tracking-[0.2em] uppercase ink-mute">{k.label}</div>
                                <div className="font-display text-3xl md:text-4xl ink mt-1">{k.value}</div>
                                <div className="flex items-center justify-between mt-2">
                                    <span className="font-data text-[10px] ink-mute">{k.unit}</span>
                                    <span className="font-data text-[11px] flex items-center gap-1" style={{ color: accent }}>
                                        <TrendingUp className="w-3 h-3" /> {k.change}
                                    </span>
                                </div>
                            </FlatCard>
                        </Reveal>
                    ))}
                </div>

                <div className="grid lg:grid-cols-12 gap-6 md:gap-8">
                    {/* Schemes */}
                    <div className="lg:col-span-7 space-y-6">
                        <FlatCard className="p-6 md:p-8" data-testid="schemes-card">
                            <div className="font-data text-[10px] tracking-[0.22em] uppercase terra mb-1">Scheme Utilisation</div>
                            <div className="font-display text-2xl md:text-3xl mb-6">Central & State Programmes</div>
                            <div className="space-y-5">
                                {d.schemes.map((s, i) => (
                                    <div key={s.name} data-testid={`scheme-${i}`}>
                                        <div className="flex items-baseline justify-between mb-1.5">
                                            <span className="font-body text-sm md:text-base">{s.name}</span>
                                            <span className="font-data text-xs ink-mute">{s.beneficiaries.toLocaleString()} beneficiaries · <span className="ink">{s.utilized}%</span></span>
                                        </div>
                                        <div className="h-[6px] bg-ivory-deep rounded-full overflow-hidden">
                                            <motion.div
                                                initial={{ width: 0 }}
                                                whileInView={{ width: `${s.utilized}%` }}
                                                viewport={{ once: true }}
                                                transition={{ duration: 1.2, delay: i * 0.08, ease: "easeOut" }}
                                                className="h-full"
                                                style={{ background: accent }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </FlatCard>

                        {/* Block dev index */}
                        <FlatCard className="p-6 md:p-8" data-testid="block-dev-table">
                            <div className="font-data text-[10px] tracking-[0.22em] uppercase terra mb-1">Block Development Index</div>
                            <div className="font-display text-2xl md:text-3xl mb-6">By Administrative Block</div>
                            <div className="overflow-x-auto">
                                <table className="w-full font-data text-sm">
                                    <thead className="hairline-b">
                                        <tr className="text-left">
                                            <th className="py-2 pr-4 font-data text-[10px] tracking-[0.18em] uppercase ink-mute">Block</th>
                                            <th className="py-2 pr-4 font-data text-[10px] tracking-[0.18em] uppercase ink-mute">Infra</th>
                                            <th className="py-2 pr-4 font-data text-[10px] tracking-[0.18em] uppercase ink-mute">Health</th>
                                            <th className="py-2 pr-4 font-data text-[10px] tracking-[0.18em] uppercase ink-mute">Edu</th>
                                            <th className="py-2 font-data text-[10px] tracking-[0.18em] uppercase ink-mute">San.</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {d.block_dev_index.map((b, i) => (
                                            <tr key={b.block} className="hairline-b last:border-b-0">
                                                <td className="py-3 pr-4 font-display text-base">{b.block}</td>
                                                <td className="py-3 pr-4">{b.infrastructure}</td>
                                                <td className="py-3 pr-4">{b.health}</td>
                                                <td className="py-3 pr-4">{b.education}</td>
                                                <td className="py-3">{b.sanitation}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </FlatCard>

                        {/* Heritage conservation */}
                        <FlatCard className="p-6 md:p-8" data-testid="heritage-conservation-table">
                            <div className="font-data text-[10px] tracking-[0.22em] uppercase terra mb-1">Heritage Conservation</div>
                            <div className="font-display text-2xl md:text-3xl mb-6">Status of Documented Sites</div>
                            <div className="space-y-3">
                                {d.heritage_conservation.map((h, i) => (
                                    <div key={h.site} className="flex items-center justify-between py-2 hairline-b last:border-b-0">
                                        <div className="font-display text-base">{h.site}</div>
                                        <div className="flex items-center gap-4">
                                            <span className="chip">{h.status}</span>
                                            <span className="font-data text-xs ink-mute">{h.last_inspection}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </FlatCard>
                    </div>

                    {/* Sidebar: SDGs + governance + feed */}
                    <div className="lg:col-span-5 space-y-6">
                        <FlatCard className="p-6 md:p-8" data-testid="sdg-card">
                            <div className="font-data text-[10px] tracking-[0.22em] uppercase terra mb-1">UN SDG Alignment</div>
                            <div className="font-display text-2xl md:text-3xl mb-6">Goals & Progress</div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
                                {d.sdg.map((s) => <SDGRing key={s.goal} {...s} />)}
                            </div>
                        </FlatCard>

                        <FlatCard className="p-6 md:p-8" data-testid="governance-card">
                            <div className="font-data text-[10px] tracking-[0.22em] uppercase terra mb-1">Governance Scores</div>
                            <div className="font-display text-2xl md:text-3xl mb-6">Six Key Indicators</div>
                            <div className="grid grid-cols-3 gap-5">
                                {d.governance_scores.map((g) => <Gauge key={g.name} percent={g.score} label={g.name} />)}
                            </div>
                        </FlatCard>

                        <FlatCard className="p-6 md:p-8" data-testid="activity-feed">
                            <div className="flex items-center justify-between mb-1">
                                <div className="font-data text-[10px] tracking-[0.22em] uppercase terra">Activity Feed</div>
                                <span className="flex items-center gap-1.5">
                                    <span className="live-dot" />
                                    <span className="font-data text-[10px] uppercase tracking-[0.18em] ink-mute">Live</span>
                                </span>
                            </div>
                            <div className="font-display text-2xl md:text-3xl mb-5">Real-time Updates</div>
                            <div className="space-y-3">
                                {feed.slice(0, 6).map((f, i) => {
                                    const Tag = f.url ? "a" : "div";
                                    const linkProps = f.url
                                        ? { href: f.url, target: "_blank", rel: "noopener noreferrer", "data-testid": `activity-link-${i}` }
                                        : {};
                                    return (
                                        <motion.div
                                            key={i}
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: i * 0.04 }}
                                            className="hairline-b last:border-b-0"
                                        >
                                            <Tag {...linkProps} className={`flex items-start gap-3 py-2 group ${f.url ? "cursor-pointer" : ""}`}>
                                                <Activity className="w-3.5 h-3.5 mt-1 shrink-0 terra" />
                                                <div className="flex-1">
                                                    <div className={`font-body text-sm leading-snug ${f.url ? "group-hover:terra transition-colors" : ""}`}>
                                                        {f.message}
                                                        {f.url && <ExternalLink className="inline w-3 h-3 ml-1.5 ink-mute group-hover:terra transition-colors" />}
                                                    </div>
                                                    <div className="font-data text-[10px] uppercase tracking-[0.16em] ink-mute mt-1">{f.time} · {f.type}</div>
                                                    {f.url && (
                                                        <div className="font-data text-[10px] tracking-[0.04em] mt-1 italic" style={{ color: accent }}>
                                                            Source: via {(() => { try { return new URL(f.url).hostname.replace(/^www\./, ""); } catch { return f.url; } })()}
                                                        </div>
                                                    )}
                                                </div>
                                            </Tag>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </FlatCard>
                    </div>
                </div>

                {/* Offices Section */}
                <section className="mt-16 md:mt-24">
                    <SectionHeader
                        kicker="Administrative Offices"
                        title="Contact The District"
                        lede="From Parliament to Panchayat — addresses, phone lines and constituencies of every elected office."
                    />
                    <div className="grid lg:grid-cols-12 gap-6">
                        {/* MP */}
                        <FlatCard className="lg:col-span-5 p-6 md:p-8" data-testid="mp-office">
                            <div className="flex items-center gap-2 font-data text-[10px] tracking-[0.22em] uppercase terra mb-2">
                                <Building className="w-3.5 h-3.5" /> Lok Sabha · MP Office
                            </div>
                            <div className="font-display text-2xl md:text-3xl leading-tight">{d.offices.mp.constituency}</div>
                            <div className="mt-4 space-y-2 font-body text-sm">
                                <div className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 mt-1 ink-mute shrink-0" /> {d.offices.mp.address}</div>
                                <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 ink-mute" /> {d.offices.mp.phone}</div>
                                <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 ink-mute" /> {d.offices.mp.email}</div>
                            </div>
                        </FlatCard>

                        {/* MLAs */}
                        <FlatCard className="lg:col-span-7 p-6 md:p-8" data-testid="mla-offices">
                            <div className="font-data text-[10px] tracking-[0.22em] uppercase terra mb-1 flex items-center gap-2"><Users className="w-3.5 h-3.5" /> Legislative Assembly · MLA Offices</div>
                            <div className="font-display text-2xl md:text-3xl mb-5">Constituencies</div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                {d.offices.mla.map((m, i) => (
                                    <div key={m.constituency} className="border hairline p-4" data-testid={`mla-${i}`}>
                                        <div className="font-data text-[10px] tracking-[0.18em] uppercase ink-mute">{m.constituency}</div>
                                        <div className="font-display text-base mt-1">{m.name}</div>
                                        <div className="font-body text-xs ink-mute mt-1">{m.address}</div>
                                        <div className="font-data text-xs mt-1">{m.phone}</div>
                                    </div>
                                ))}
                            </div>
                        </FlatCard>

                        {/* Panchayats */}
                        <FlatCard className="lg:col-span-12 p-6 md:p-8" data-testid="panchayat-offices">
                            <div className="font-data text-[10px] tracking-[0.22em] uppercase terra mb-1 flex items-center gap-2"><ScrollText className="w-3.5 h-3.5" /> Panchayati Raj · Local Self-Government</div>
                            <div className="font-display text-2xl md:text-3xl mb-5">Block & Zila Panchayats</div>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {d.offices.panchayat.map((p, i) => (
                                    <div key={p.name} className="border hairline p-4" data-testid={`panchayat-${i}`}>
                                        <div className="font-display text-lg leading-tight">{p.name}</div>
                                        <div className="font-data text-[10px] uppercase tracking-[0.16em] ink-mute mt-1">{p.head}</div>
                                        <div className="font-body text-xs ink-mute mt-2">{p.address}</div>
                                        <div className="font-data text-xs">{p.phone}</div>
                                    </div>
                                ))}
                            </div>
                        </FlatCard>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default Dashboard;
